import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

export default function SobrePage() {
  const { t } = useTranslation('sobre');

  useEffect(() => {
    // Set SEO meta tags
    document.title = "Sobre a FGT Capital | Engenharia Patrimonial São Paulo";
    
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "Sobre a FGT Capital",
      "description": "Conheça a FGT Capital, especialista em engenharia patrimonial com rigor técnico em São Paulo. Nossa história, valores e princípios.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/sobre`,
      "mainEntity": {
        "@type": "Organization",
        "name": "FGT Capital",
        "foundingDate": "2016",
        "description": "Empresa especializada em engenharia patrimonial com foco em consórcios regulados e planejamento patrimonial.",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "São Paulo",
          "addressRegion": "SP", 
          "addressCountry": "BR"
        },
        "knowsAbout": ["Engenharia Patrimonial", "Consórcios Regulados", "Planejamento Patrimonial", "Aquisição Imobiliária"]
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const principles = [
    {
      number: '01',
      title: t('principles.0.title'),
      description: t('principles.0.description'),
    },
    {
      number: '02',
      title: t('principles.1.title'),
      description: t('principles.1.description'),
    },
    {
      number: '03',
      title: t('principles.2.title'),
      description: t('principles.2.description'),
    },
  ];

  return (
    <div className="min-h-screen bg-[#F6F2EC]">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://public.readdy.ai/ai/img_res/9a235b03396fd927bda923d1d2654517.jpg"
            alt="Sobre a FGT"
            className="w-full h-full object-cover object-top"
          />
        </div>
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B]/90 via-[#0B0B0B]/50 to-[#0B0B0B]/20"></div>
        {/* Accent line */}
        <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#F97700]"></div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pb-20 pt-48">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#F97700] mb-6">
              {t('hero.label')}
            </p>
            <h1 className="text-5xl md:text-7xl font-semibold text-white leading-[1.05] mb-8 drop-shadow-lg">
              {t('hero.title')}
            </h1>
            <p className="text-xl text-white/80 leading-relaxed max-w-2xl mx-auto">
              {t('hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-32 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-[#2E2E2E] leading-relaxed mb-8">
              {t('content.paragraph1')}
            </p>
            <p className="text-lg text-[#2E2E2E] leading-relaxed mb-8">
              {t('content.paragraph2')}
            </p>
            <p className="text-lg text-[#2E2E2E] leading-relaxed">
              {t('content.paragraph3')}
            </p>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="pb-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-semibold text-[#0B0B0B] leading-tight mb-6">
              {t('principles.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {principles.map((principle, index) => (
              <div
                key={index}
                className="bg-[#FBF8F3] border border-[#E7E1D9] p-10 hover:border-[#F97700] transition-all duration-300"
              >
                <div className="text-6xl font-semibold text-[#F97700] mb-6">
                  {principle.number}
                </div>
                <h3 className="text-2xl font-semibold text-[#0B0B0B] mb-4">
                  {principle.title}
                </h3>
                <p className="text-[#2E2E2E] leading-relaxed">
                  {principle.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="pb-32 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#0B0B0B] p-12 md:p-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-white leading-tight mb-8">
              {t('values.title')}
            </h2>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-2 h-2 bg-[#F97700] mt-2 flex-shrink-0"></div>
                <p className="text-lg text-white/90 leading-relaxed">
                  {t('values.item1')}
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-2 h-2 bg-[#F97700] mt-2 flex-shrink-0"></div>
                <p className="text-lg text-white/90 leading-relaxed">
                  {t('values.item2')}
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-2 h-2 bg-[#F97700] mt-2 flex-shrink-0"></div>
                <p className="text-lg text-white/90 leading-relaxed">
                  {t('values.item3')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6 bg-[#0B0B0B]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-semibold text-white leading-tight mb-8">
            {t('cta.title')}
          </h2>
          <p className="text-lg text-white/80 mb-12 max-w-2xl mx-auto">
            {t('cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/diagnostico"
              className="inline-flex items-center justify-center px-8 py-4 bg-[#F97700] text-white font-semibold hover:bg-[#E56E00] transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('cta.primary')}
            </Link>
            <a
              href="https://wa.me/5511930322408"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#0B0B0B] font-semibold hover:bg-white/90 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line mr-2"></i>
              {t('cta.secondary')}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}